import type {
  AirshowJunctionPreviewRequest,
  AirshowJunctionPreviewAvailable,
  AirshowJunctionPreviewResponse,
  AirshowJunctionPreviewUnavailable,
  AirshowJunctionSelection,
} from "./airshowJunctionContract";

export type AirshowJunctionLayout = "split" | "overlay";
export type AirshowJunctionShading = "solid" | "wireframe" | "normals";
export type AirshowJunctionArtifactState = "loading" | "ready" | "unavailable";

export interface AirshowJunctionViewerState {
  modelId: string;
  layout: AirshowJunctionLayout;
  shading: AirshowJunctionShading;
  symmetricOpacity: number;
  cameraSync: { synchronized: true; signature: string; revision: number };
  artifacts: {
    baseline: { status: AirshowJunctionArtifactState; renderedMeshCount: number };
    result: { status: AirshowJunctionArtifactState; renderedMeshCount: number };
  };
}

export interface AirshowJunctionViewEvidence {
  schema: "loom.airshow-junction-view-evidence/v1";
  preview_bundle_key: string | null;
  request: AirshowJunctionPreviewRequest;
  layout: AirshowJunctionLayout;
  shading: AirshowJunctionShading;
  symmetric_opacity: number;
  camera_sync: { synchronized: true; signature: string; revision: number };
  artifacts: {
    baseline: { status: "ready" | "unavailable"; rendered_mesh_count: number };
    result: { status: "ready" | "unavailable"; rendered_mesh_count: number };
  };
}

type AirshowJunctionFeedbackResult =
  | Omit<AirshowJunctionPreviewAvailable, "baseline_url" | "result_url" | "metadata_url" | "bbox">
  | Omit<AirshowJunctionPreviewUnavailable, "retryable">;

export interface AirshowJunctionFeedbackEvidence {
  schema: "loom.airshow-junction-feedback-evidence/v1";
  trust: "client-reported-unattested";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  review_bundle_key: string;
  preview_bundle_key: string | null;
  junction_id: string | null;
  selection: AirshowJunctionSelection | null;
  result: AirshowJunctionFeedbackResult;
  view: AirshowJunctionViewEvidence;
}

export function airshowJunctionViewModelId(
  request: AirshowJunctionPreviewRequest,
  previewBundleKey: string | null,
): string {
  return JSON.stringify([request, previewBundleKey]);
}

export function createAirshowJunctionViewEvidence(input: {
  request: AirshowJunctionPreviewRequest;
  response: AirshowJunctionPreviewResponse;
  viewer: AirshowJunctionViewerState | null;
}): AirshowJunctionViewEvidence | null {
  const { request, response, viewer } = input;
  if (!viewer || viewer.modelId !== airshowJunctionViewModelId(request, response.preview_bundle_key)
      || !Number.isFinite(viewer.symmetricOpacity) || viewer.symmetricOpacity < 0.1
      || viewer.symmetricOpacity > 1 || !viewer.cameraSync.signature
      || !Number.isInteger(viewer.cameraSync.revision) || viewer.cameraSync.revision < 0) return null;
  const sides = [viewer.artifacts.baseline, viewer.artifacts.result];
  const available = response.preview.status === "available";
  if (available && sides.some(({ status, renderedMeshCount }) => (
    status !== "ready" || !Number.isInteger(renderedMeshCount) || renderedMeshCount < 1
  ))) return null;
  if (!available && sides.some(({ status, renderedMeshCount }) => (
    status !== "unavailable" || renderedMeshCount !== 0
  ))) return null;
  const artifact = (side: (typeof sides)[number]) => ({
    status: side.status as "ready" | "unavailable",
    rendered_mesh_count: side.renderedMeshCount,
  });
  return {
    schema: "loom.airshow-junction-view-evidence/v1",
    preview_bundle_key: response.preview_bundle_key,
    request: { ...request },
    layout: viewer.layout,
    shading: viewer.shading,
    symmetric_opacity: viewer.symmetricOpacity,
    camera_sync: { ...viewer.cameraSync },
    artifacts: {
      baseline: artifact(viewer.artifacts.baseline),
      result: artifact(viewer.artifacts.result),
    },
  };
}

export function createAirshowJunctionFeedbackEvidence(
  response: AirshowJunctionPreviewResponse,
  view: AirshowJunctionViewEvidence,
): AirshowJunctionFeedbackEvidence {
  const unavailable = response.preview.status === "unavailable";
  if (view.preview_bundle_key !== response.preview_bundle_key) {
    throw new Error("Airshow junction feedback view bundle does not match its preview response");
  }
  const selection = unavailable ? null : response.selection;
  const junction = selection ? response.junctions.find(({ component_a: a, component_b: b }) => (
    (a === selection.component_a && b === selection.component_b)
    || (b === selection.component_a && a === selection.component_b)
  )) : null;
  const result: AirshowJunctionFeedbackResult = response.preview.status === "available" ? {
    status: "available",
    representation: response.preview.representation,
    fidelity: response.preview.fidelity,
    continuity: response.preview.continuity,
    radius_semantics: response.preview.radius_semantics,
    accepted: false,
    exportable: false,
    baseline: { ...response.preview.baseline },
    result: { ...response.preview.result },
    warnings: [...response.preview.warnings],
  } : {
    status: "unavailable",
    representation: "unavailable",
    fidelity: "unavailable",
    code: response.preview.code,
    message: response.preview.message,
    warnings: [...response.preview.warnings],
  };
  return {
    schema: "loom.airshow-junction-feedback-evidence/v1",
    trust: "client-reported-unattested",
    entry_id: response.entry_id,
    archive_member_path: response.archive_member_path,
    source_sha256: response.source_sha256,
    review_bundle_key: response.review_bundle_key,
    preview_bundle_key: unavailable ? null : response.preview_bundle_key,
    junction_id: unavailable ? null : junction?.id ?? null,
    selection: unavailable ? null : { ...selection! },
    result,
    view,
  };
}
